import { BookingLocationType } from '@navio/nsdk';
import { cn, cva, NoddiIcon, type NoddiIconName, type VariantProps } from 'noddi-ui-common';

/**
 * Which of the two channels a record belongs to. Deliberately not the rounded pill used for
 * booking status: a channel is a fact about the record, a status is a state it passes through.
 * Colored from the `channel-*` theme scales, the one place a channel's color is set.
 */
const channelBadgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center gap-[5px] rounded-md font-medium whitespace-nowrap',
  {
    variants: {
      channel: {
        [BookingLocationType.STATIONARY]: '',
        [BookingLocationType.MOBILE]: ''
      },
      tone: {
        soft: '',
        solid: 'text-white',
        outline: 'border bg-transparent',
        iconOnly: ''
      },
      size: {
        md: 'h-[22px] px-1.5 text-[11px]',
        sm: 'h-[18px] px-1 text-[10px]'
      }
    },
    compoundVariants: [
      {
        channel: BookingLocationType.STATIONARY,
        tone: 'soft',
        className: 'bg-channel-stationary-100 text-channel-stationary-800'
      },
      { channel: BookingLocationType.STATIONARY, tone: 'solid', className: 'bg-channel-stationary-700' },
      {
        channel: BookingLocationType.STATIONARY,
        tone: 'outline',
        className: 'border-channel-stationary-700 text-channel-stationary-700'
      },
      {
        channel: BookingLocationType.STATIONARY,
        tone: 'iconOnly',
        className: 'bg-channel-stationary-100 text-channel-stationary-800'
      },
      { channel: BookingLocationType.MOBILE, tone: 'soft', className: 'bg-channel-mobile-100 text-channel-mobile-800' },
      { channel: BookingLocationType.MOBILE, tone: 'solid', className: 'bg-channel-mobile-700' },
      {
        channel: BookingLocationType.MOBILE,
        tone: 'outline',
        className: 'border-channel-mobile-700 text-channel-mobile-700'
      },
      {
        channel: BookingLocationType.MOBILE,
        tone: 'iconOnly',
        className: 'bg-channel-mobile-100 text-channel-mobile-800'
      },
      // Square in the icon-only tone, so the icon keeps the same optical weight as the labelled badge.
      { tone: 'iconOnly', size: 'md', className: 'w-[22px] justify-center px-0' },
      { tone: 'iconOnly', size: 'sm', className: 'w-[18px] justify-center px-0' }
    ],
    defaultVariants: {
      tone: 'soft',
      size: 'md'
    }
  }
);

export const CHANNEL_ICON = {
  [BookingLocationType.STATIONARY]: 'Store',
  [BookingLocationType.MOBILE]: 'Truck'
} as const satisfies Record<BookingLocationType, NoddiIconName>;

export const CHANNEL_LABEL = {
  [BookingLocationType.STATIONARY]: 'Service center',
  [BookingLocationType.MOBILE]: 'Mobile'
} as const satisfies Record<BookingLocationType, string>;

type Props = Omit<VariantProps<typeof channelBadgeVariants>, 'channel'> & {
  channel: BookingLocationType;
  /** Overrides the default channel name — use only where the surface already implies "channel". */
  label?: string;
  className?: string;
};

export const ChannelBadge = ({ channel, tone = 'soft', size = 'md', label, className }: Props) => {
  const text = label ?? CHANNEL_LABEL[channel];

  return (
    <span
      className={cn(channelBadgeVariants({ channel, tone, size }), className)}
      // The icon-only tone drops the text, so the name has to survive somewhere for screen readers.
      aria-label={tone === 'iconOnly' ? text : undefined}
      title={tone === 'iconOnly' ? text : undefined}
    >
      <NoddiIcon name={CHANNEL_ICON[channel]} size={size === 'sm' ? 'xSmall' : 'small'} />
      {tone !== 'iconOnly' && text}
    </span>
  );
};
